import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArrowRight, Building, Users, Shield, Headphones, BarChart3, Settings, Globe } from 'lucide-react';

const FEATURE_KEYS = [
  { key: 'team_members', icon: Users },
  { key: 'security', icon: Shield },
  { key: 'admin_panel', icon: Settings },
  { key: 'analytics', icon: BarChart3 },
  { key: 'multilingual', icon: Globe },
  { key: 'support', icon: Headphones },
  { key: 'onboarding', icon: Building },
];

const EnterprisePage = () => {
  const { t } = useTranslation('landing');

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO
        title={t('enterprise.seo.title')}
        description={t('enterprise.seo.description')}
        canonical="/enterprise"
        breadcrumbs={[
          { name: t('pricing.breadcrumb_home'), url: '/' },
          { name: t('enterprise.breadcrumb') }
        ]}
      />
      <Navbar />

      <main className="pt-20 pb-24">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 py-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 mb-6">
            <Building className="w-4 h-4" />
            <span className="text-sm font-medium">{t('enterprise.badge')}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {t('enterprise.headline_a')}<br />{t('enterprise.headline_b')}
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            {t('enterprise.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="bg-amber-500 text-black px-8 py-4 rounded-xl font-semibold hover:bg-amber-400 transition-colors flex items-center gap-2">
              {t('enterprise.cta_primary')}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/pricing" className="border border-gray-700 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/5 transition-colors">
              {t('enterprise.cta_secondary')}
            </Link>
          </div>
        </section>

        {/* Features Grid */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURE_KEYS.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.key} className="p-6 bg-white/5 rounded-2xl border border-gray-800 hover:border-amber-500/30 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{t(`enterprise.features.${feature.key}.title`)}</h3>
                  <p className="text-gray-400">{t(`enterprise.features.${feature.key}.desc`)}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 rounded-2xl border border-gray-800 p-12">
            <h2 className="text-3xl font-bold text-white mb-4">{t('enterprise.cta_box.headline')}</h2>
            <p className="text-gray-400 mb-8">{t('enterprise.cta_box.subtitle')}</p>
            <Link to="/contact" className="bg-amber-500 text-black px-8 py-4 rounded-xl font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
              {t('enterprise.cta_box.button')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default EnterprisePage;
